import { useMemo } from 'react';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { ConfirmationDialogV2, ErpDataTableV2, ErpInputTableV2 } from '@/components/custom/erp';
import { getDataTableColumns, getDetailDataTableColumns } from './cols-definition';
import { ActionBar, FormDialog, SearchDialog } from './components';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

export default function PhieuNhapDieuChinhGiaHangMuaPage() {
  const {
    data,
    isLoading,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    detail,
    fetchDetail,
    currentPage,
    handlePageChange,
    totalItems
  } = useCRUD({
    endpoint: QUERY_KEYS.PHIEU_NHAP_DIEU_CHINH_GIA_HANG_MUA
  });
  const {
    showForm,
    showDelete,
    showSearch,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,
    handleRowClick: originalHandleRowClick,
    clearSelection,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleSearch,
    handleCloseSearch
  } = useForms(fetchDetail);

  const handleRowClick = async (params: MuaHangRecord) => {
    originalHandleRowClick(params);
  };

  const handleSearchSubmit = (filters: MuaHangRecord) => {
    return filters;
  };

  const handleSubmit = async (data: MuaHangRecord) => {
    try {
      if (formMode === 'add') {
        await addItem(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updateItem(selectedObj.uuid, { ...data, i_so_ct: selectedObj.i_so_ct });
      }
      handleCloseForm();
      clearSelection();
    } catch (error) {
      return;
    }
  };
  const mainColumns = useMemo(() => getDataTableColumns(handleViewClick), [handleViewClick]);

  const tables = [
    {
      name: 'Tất cả',
      rows: data,
      columns: mainColumns
    },
    {
      name: 'Chưa ghi sổ',
      rows: data?.filter(row => row.status === '0'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#EAD1DC]' />
    },
    {
      name: 'Chờ duyệt',
      rows: data?.filter(row => row.status === '3'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-red-500' />
    },
    {
      name: 'Đã ghi sổ',
      rows: data?.filter(row => row.status === '5'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#3D85C6]' />
    }
  ];

  return (
    <div className='flex h-screen w-screen flex-col'>
      {showSearch && <SearchDialog open={showSearch} onClose={handleCloseSearch} onSearch={handleSearchSubmit} />}

      {showForm && (
        <FormDialog
          formMode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onSubmit={handleSubmit}
          onAdd={handleAddClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onCopy={handleCopyClick}
        />
      )}

      {!showForm && (
        <>
          <ActionBar
            onAdd={handleAddClick}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onCopy={handleCopyClick}
            onSearch={handleSearch}
            onRefresh={async () => {
              await refreshData();
              await fetchDetail(selectedObj?.uuid);
            }}
            isEditDisabled={!selectedObj}
          />

          <ResizablePanelGroup direction='vertical' className='min-h-0 flex-1 overflow-hidden'>
            <ResizablePanel defaultSize={50} minSize={20}>
              <div data-perf-id='pndc-gia-hang-mua-master-table' className='h-full w-full overflow-hidden'>
                <ErpDataTableV2
                  tables={tables}
                  onRowClick={handleRowClick}
                  selectedRowId={selectedRowIndex || undefined}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  totalItems={totalItems}
                  loading={isLoading}
                />
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={50} minSize={20}>
              <div data-perf-id='pndc-gia-hang-mua-bottom-detail-table' className='h-full overflow-hidden'>
                <ErpInputTableV2
                  rows={selectedObj && !isLoading ? detail?.chi_tiet_data || [] : []}
                  columns={getDetailDataTableColumns}
                  showIndexColumn
                  actionsDisabled={formMode === 'view' || !selectedObj}
                  height={'100%'}
                  loading={isLoading}
                />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </>
      )}
      {showDelete && (
        <ConfirmationDialogV2
          open={showDelete}
          onClose={handleCloseDelete}
          onConfirm={() => {
            deleteItem(selectedObj!.uuid);
            handleCloseDelete();
            clearSelection();
            handleCloseForm();
          }}
        />
      )}
    </div>
  );
}
